import { graphql } from "gatsby"
import React, { useState } from "react"
import { Helmet } from "react-helmet"
import styled, { css } from "styled-components"
import breakpoint from "styled-components-breakpoint"
import { Margin, mb, Padding } from "styled-components-spacing"

import { P } from "../components/atoms/Elements"
import { Select } from "../components/atoms/FormFields"
import { H1, H3 } from "../components/atoms/Headings"
import { Link } from "../components/atoms/Link"
import { SectionTitle } from "../components/collection/sectionTitle"
import Layout from "../components/Layout"
import SEO from "../components/SEO"
import { WorkbookSlat } from "../components/template/WorkbookSlat"
import headerBackground from "../images/kpi-banner.jpg"

const LinkResolver = require("../utils/linkResolver")

const Header = styled.header`
  background: ${props => props.theme.colors.coral} url("${headerBackground}")
    50% 50%;
  background-size: cover;
  border-radius: 4px;
  min-height: 230px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  ${mb(3)};
  padding: 24px 40px;
`

const KPIWorkbookListPage = props => {
  let kpiWorkbooks = [...props.data.allContentfulKpiWorkbook.nodes]
  const SORT_OPTIONS = {
    NAME: "Name",
    NEWEST: "Newest",
  }

  const [sortBy, setSortBy] = useState(SORT_OPTIONS.DOWNLOADS)

  //split them into 2 groups: Global and local.
  // We assume that local groups
  let kpiWorkbooksArray = []

  kpiWorkbooks.forEach(node => {
    kpiWorkbooksArray.push(node)
  })

  const sortTemplates = (a, b) => {
    const avgRating = arr => {
      if (!arr) return 0
      return arr.reduce((a, b) => parseInt(a) + parseInt(b), 0) / arr.length
    }

    switch (sortBy) {
      case SORT_OPTIONS.NAME:
        return a.title > b.title ? 1 : -1
      case SORT_OPTIONS.RATINGS:
        return avgRating(b.ratings) - avgRating(a.ratings)
      case SORT_OPTIONS.NEWEST:
      default:
        return b.createdAt - a.createdAt
    }
  }

  kpiWorkbooksArray.sort(sortTemplates)

  const FilterBar = () => {
    const titleString = `${kpiWorkbooksArray.length} KPI Dashboard${
      kpiWorkbooksArray.length !== 1 ? `s` : ``
    }`

    return (
      <SectionTitle>
        <H3>{titleString}</H3>
        <div>
          <strong
            css={breakpoint(
              `xs`,
              `sm`
            )(
              css`
                display: none;
              `
            )}
          >
            Sort by
          </strong>{" "}
          <Select
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
            css={css`
              margin: 0 0 0 8px;
              width: 200px;
            `}
          >
            {Object.keys(SORT_OPTIONS).map(optionID => (
              <option key={optionID} value={SORT_OPTIONS[optionID]}>
                {SORT_OPTIONS[optionID]}
              </option>
            ))}
          </Select>
        </div>
      </SectionTitle>
    )
  }
  const KPIWorkbooksList = () => {
    if (!kpiWorkbooksArray.length) return null

    return (
      <Margin bottom={1}>
        {kpiWorkbooksArray.map((kpiWorkbook, i) => (
          <WorkbookSlat
            first={i === 0}
            key={kpiWorkbook.slug}
            workbook={kpiWorkbook}
          />
        ))}
      </Margin>
    )
  }

  const schema = {
    "@context": "https://schema.org/",
    "@type": "ItemList",
    itemListElement: [
      [...kpiWorkbooksArray].map((kpiWorkbook, i) => ({
        "@type": "ListItem",
        position: i,
        item: {
          "@type": "Product",
          name: kpiWorkbook.title,
          url: LinkResolver(kpiWorkbook.internal.type, kpiWorkbook.slug),
          manufacturer: kpiWorkbook.createdBy
            ? kpiWorkbook.createdBy.map(creator => ({
                "@type": "Corporation",
                name: creator.name,
              }))
            : null,
          image:
            kpiWorkbook.seoImage?.OGImage?.src ||
            props.data.fallbackImage.childImageSharp.OGImage.src,
          sku: kpiWorkbook.contentfulId,
          offers: {
            url: LinkResolver(kpiWorkbook.internal.type, kpiWorkbook.slug),
            price: "0",
            priceCurrency: "USD",
            businessFunction: "Add to Karbon",
          },
        },
      })),
    ],
  }

  return (
    <Layout menuTitle={props.pageContext?.menuTitle} hideRegionSelector>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>
      <SEO
        title={"KPI Dashboards"}
        description={`Data-driven decision making across every level of your firm.`}
        pagePath={"/kpi-dashboards/"}
      />
      <Padding bottom={4}>
        <Header>
          <H1
            mb={1}
            css={css`
              font-size: 32px;
            `}
          >
            Karbon Practice Intelligence Dashboards
          </H1>
          <P mb={0}>
            Data-driven decision making across every level of your firm
          </P>
        </Header>
        <FilterBar />
        <KPIWorkbooksList />

        {kpiWorkbooks.length === 0 && (
          <p>
            No dashboards found that match your filters.
            <br />
            <Link underlined to={"/"}>
              Reset filters
            </Link>
          </p>
        )}
      </Padding>
    </Layout>
  )
}

export const query = graphql`
  query {
    fallbackImage: file(name: { eq: "fallback-image" }) {
      childImageSharp {
        OGImage: resize(width: 1100, height: 572, quality: 100) {
          src
        }
      }
    }
    allContentfulKpiWorkbook(
      filter: { title: { ne: "__schema__" } }
      sort: { fields: title }
    ) {
      nodes {
        ...ContentfulKpiWorkbookSlatFragment
      }
    }
  }
`

export default KPIWorkbookListPage
